import { useTranslation } from 'react-i18next';
import { History, CalendarClock } from 'lucide-react';
import SectionTitle from '../../../components/SectionTitle/SectionTitle';
import { CardV2, CardHeader, CardTitle, CardContent } from '../../../components/CardV2/CardV2';
import { useVisitsInsights } from './useVisitsInsights';

type Props = { timeRange: { start: string; end: string } };

function BreakdownRow({ label, value, loading }: { label: string; value: number; loading: boolean }) {
  const display = loading ? '—' : value.toLocaleString();
  return (
    <div className="flex items-center justify-between gap-4 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="tabular-nums font-medium text-foreground">{display}</span>
    </div>
  );
}

function HeroNumber({ value, loading }: { value: number; loading: boolean }) {
  return (
    <p className={`text-2xl font-bold tabular-nums text-foreground ${loading ? 'text-muted-foreground' : ''}`}>
      {loading ? '—' : value.toLocaleString()}
    </p>
  );
}

export default function AppointmentVisitsInsightsSection({ timeRange }: Props) {
  const { t } = useTranslation();
  const { data, loading, error } = useVisitsInsights(timeRange);

  const past = data?.past;
  const upcoming = data?.upcoming;
  const headlineUpcoming = data?.count ?? upcoming?.total ?? 0;

  return (
    <div className="space-y-4">
      <SectionTitle text={t('stats.visits_insights.section_title')} />
      {error && (
        <p className="text-sm text-muted-foreground">{t('stats.visits_insights.load_failed')}</p>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <CardV2>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <History className="h-5 w-5" />
              {t('stats.visits_insights.past_visits')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1">
              <HeroNumber value={past?.total ?? 0} loading={loading && !error} />
              <p className="text-sm text-muted-foreground">{t('stats.visits_insights.past_visits_total_hint')}</p>
            </div>
            <div className="border-t border-border pt-3 space-y-2">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                {t('stats.visits_insights.breakdown')}
              </p>
              <p className="text-xs text-muted-foreground">{t('stats.visits_insights.breakdown_hint')}</p>
              <BreakdownRow label={t('stats.visits_insights.presented')} value={past?.presented ?? 0} loading={loading && !error} />
              <BreakdownRow label={t('stats.visits_insights.cancelled')} value={past?.cancelled ?? 0} loading={loading && !error} />
              <BreakdownRow label={t('stats.visits_insights.net_brides')} value={past?.netBrides ?? 0} loading={loading && !error} />
            </div>
          </CardContent>
        </CardV2>

        <CardV2>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CalendarClock className="h-5 w-5" />
              {t('stats.visits_insights.upcoming_visits')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1">
              <HeroNumber value={headlineUpcoming} loading={loading && !error} />
              <p className="text-sm text-muted-foreground">{t('stats.visits_insights.upcoming_visits_total_hint')}</p>
            </div>
            <div className="border-t border-border pt-3 space-y-2">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                {t('stats.visits_insights.scheduled_breakdown')}
              </p>
              <BreakdownRow label={t('stats.visits_insights.scheduled_first_visit')} value={upcoming?.firstVisit ?? 0} loading={loading && !error} />
              <BreakdownRow label={t('stats.visits_insights.scheduled_second_visit')} value={upcoming?.secondVisit ?? 0} loading={loading && !error} />
              <BreakdownRow label={t('stats.visits_insights.scheduled_third_visit')} value={upcoming?.thirdVisit ?? 0} loading={loading && !error} />
              <BreakdownRow label={t('stats.visits_insights.scheduled_fittings_total')} value={upcoming?.fittingsTotal ?? 0} loading={loading && !error} />
            </div>
          </CardContent>
        </CardV2>
      </div>
    </div>
  );
}
